/**
 * Styled text in slide fields. The inline markup every renderer reads (editor, PDF, PPTX):
 *   **bold**   *italic*   ++larger++   ~~smaller~~   {c:#E4405F;w:800;i;f:Playfair Display|styled text}
 * The style span carries colour (c), weight (w, 100–900), italic (i) and font (f); its text may hold the other
 * markers. The editor formats on runs (text + attributes) and writes the markup back, so styling a selection
 * never nests tags or leaves markers in the text.
 */

export interface SpanStyle { c?: string; w?: number; i?: boolean; f?: string }
export interface Run { text: string; b?: boolean; it?: boolean; sz?: 'up' | 'down'; st?: SpanStyle }

const STYLE_PART = '(?:c:#[0-9a-fA-F]{3,8}|w:[1-9]00|i|f:[A-Za-z0-9 ]{2,40})';
/** `{style|text}` — group 1 the style, group 2 the text. */
export const STYLE_TOKEN = `\\{(${STYLE_PART}(?:;${STYLE_PART})*)\\|([^{}\\n]+)\\}`;
const TOKEN_SRC = `${STYLE_TOKEN}|\\*\\*[^*]+\\*\\*|\\*[^*\\n]+\\*|\\+\\+[^+\\n][^+]*?\\+\\+|~~[^~\\n][^~]*?~~|\\n`;
export const tokenRe = () => new RegExp(TOKEN_SRC, 'g');
/** Does the text carry any markup (so a plain render would show markers)? */
export const hasMarkup = (s: string) => /\*\*|\*[^*\n]+\*|\+\+|~~|\n/.test(s) || new RegExp(STYLE_TOKEN).test(s);

export function parseStyle(src: string): SpanStyle {
  const st: SpanStyle = {};
  for (const part of src.split(';')) {
    if (part === 'i') st.i = true;
    else if (part.startsWith('c:')) st.c = part.slice(2);
    else if (part.startsWith('w:')) st.w = Number(part.slice(2));
    else if (part.startsWith('f:')) st.f = part.slice(2).trim();
  }
  return st;
}
export function styleKey(st?: SpanStyle): string {
  if (!st) return '';
  const parts: string[] = [];
  if (st.c && /^#[0-9a-fA-F]{3,8}$/.test(st.c)) parts.push(`c:${st.c}`);
  if (st.w && st.w >= 100 && st.w <= 900 && st.w % 100 === 0) parts.push(`w:${st.w}`);
  if (st.i) parts.push('i');
  if (st.f && /^[A-Za-z0-9 ]{2,40}$/.test(st.f)) parts.push(`f:${st.f}`);
  return parts.join(';');
}
/** CSS declarations for a style span (inline `style=""` form). */
export function styleCssText(st: SpanStyle): string {
  return [
    st.c ? `color:${st.c}` : '',
    st.w ? `font-weight:${st.w}` : '',
    st.i ? 'font-style:italic' : '',
    st.f ? `font-family:'${st.f}', system-ui, sans-serif` : '',
  ].filter(Boolean).join(';');
}
/** Every font a text names in its style spans (to load them). */
export function fontsInText(text: string): string[] {
  const out = new Set<string>();
  const re = new RegExp(STYLE_TOKEN, 'g'); let m: RegExpExecArray | null;
  while ((m = re.exec(text))) { const f = parseStyle(m[1]).f; if (f) out.add(f); }
  return [...out];
}
/** Markup → plain words (style spans and markers dropped). */
export function plainText(text: string): string {
  return markupToRuns(text).map((r) => r.text).join('');
}

// ─── Markup ⇄ runs ──────────────────────────────────────────────────────────

const sameAttrs = (a: Run, b: Run) => !!a.b === !!b.b && !!a.it === !!b.it && a.sz === b.sz && styleKey(a.st) === styleKey(b.st);

export function markupToRuns(text: string, base: Omit<Run, 'text'> = {}): Run[] {
  const out: Run[] = [];
  const re = tokenRe(); let last = 0; let m: RegExpExecArray | null;
  const push = (t: string, a: Omit<Run, 'text'> = base) => { if (t) out.push({ text: t, ...a }); };
  while ((m = re.exec(text))) {
    if (m.index > last) push(text.slice(last, m.index));
    const tok = m[0];
    if (tok === '\n') out.push({ text: '\n' });
    else if (m[1] !== undefined) out.push(...markupToRuns(m[2], { ...base, st: { ...base.st, ...parseStyle(m[1]) } }));
    else if (tok.startsWith('**')) out.push(...markupToRuns(tok.slice(2, -2), { ...base, b: true }));
    else if (tok.startsWith('++')) out.push(...markupToRuns(tok.slice(2, -2), { ...base, sz: 'up' }));
    else if (tok.startsWith('~~')) out.push(...markupToRuns(tok.slice(2, -2), { ...base, sz: 'down' }));
    else out.push(...markupToRuns(tok.slice(1, -1), { ...base, it: true }));
    last = m.index + tok.length;
  }
  if (last < text.length) push(text.slice(last));
  return out;
}

/** Adjacent runs with the same attributes become one; empty runs go. */
export function normalizeRuns(runs: Run[]): Run[] {
  const out: Run[] = [];
  for (const r of runs) {
    if (!r.text) continue;
    const prev = out[out.length - 1];
    if (prev && r.text !== '\n' && prev.text !== '\n' && sameAttrs(prev, r)) prev.text += r.text;
    else out.push({ ...r, st: r.st && styleKey(r.st) ? { ...r.st } : undefined });
  }
  return out;
}

/**
 * Runs → markup. Markers nest bold › italic › size › style span; italic inside bold is written as the span's
 * `i` (`***x***` is not markup any renderer reads). Markers never cross a line break.
 */
export function runsToMarkup(input: Run[]): string {
  const runs = normalizeRuns(input.map((r) => (r.b && r.it ? { ...r, it: false, st: { ...r.st, i: true } } : r)));
  const clean = (t: string, inSpan: boolean) => (inSpan ? t.replace(/[*{}]/g, '') : t.replace(/\*/g, ''));
  const groups = <K>(rs: Run[], key: (r: Run) => K) => {
    const gs: Array<{ k: K; rs: Run[] }> = [];
    for (const r of rs) { const k = key(r); const g = gs[gs.length - 1]; if (g && g.k === k) g.rs.push(r); else gs.push({ k, rs: [r] }); }
    return gs;
  };
  const textOf = (rs: Run[]) => rs.map((r) => r.text).join('');
  const wrap = (open: string, close: string, rs: Run[], inner: string) => (textOf(rs).trim() ? `${open}${inner}${close}` : inner);
  const spans = (rs: Run[]) => groups(rs, (r) => styleKey(r.st)).map(({ k, rs: g }) => (k && textOf(g).trim() ? `{${k}|${clean(textOf(g), true)}}` : clean(textOf(g), false))).join('');
  const sizes = (rs: Run[]) => groups(rs, (r) => r.sz || '').map(({ k, rs: g }) => (k === 'up' ? wrap('++', '++', g, spans(g)) : k === 'down' ? wrap('~~', '~~', g, spans(g)) : spans(g))).join('');
  const italics = (rs: Run[]) => groups(rs, (r) => !!r.it).map(({ k, rs: g }) => (k ? wrap('*', '*', g, sizes(g)) : sizes(g))).join('');
  const bolds = (rs: Run[]) => groups(rs, (r) => !!r.b).map(({ k, rs: g }) => (k ? wrap('**', '**', g, italics(g)) : italics(g))).join('');
  // Line by line: no marker spans a newline.
  const lines: Run[][] = [[]];
  for (const r of runs) { if (r.text === '\n') lines.push([]); else lines[lines.length - 1].push(r); }
  return lines.map(bolds).join('\n');
}

// ─── Markup → HTML (editor field) ───────────────────────────────────────────

const esc = (t: string) => t.replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c] as string));

/** Markup → HTML for the editable field: **b** → <b>, *i* → <i>, ++x++ / ~~x~~ → sized spans, style spans, newlines → <br>. */
export function richHtml(text: string): string {
  const re = tokenRe(); let out = ''; let last = 0; let m: RegExpExecArray | null;
  while ((m = re.exec(text))) {
    if (m.index > last) out += esc(text.slice(last, m.index));
    const tok = m[0];
    if (tok === '\n') out += '<br>';
    else if (m[1] !== undefined) { const st = parseStyle(m[1]); out += `<span data-st="${styleKey(st)}" style="${styleCssText(st).replace(/"/g, '')}">${richHtml(m[2])}</span>`; }
    else if (tok.startsWith('**')) out += `<b>${richHtml(tok.slice(2, -2))}</b>`;
    else if (tok.startsWith('++')) out += `<span data-size="up" style="font-size:1.28em">${richHtml(tok.slice(2, -2))}</span>`;
    else if (tok.startsWith('~~')) out += `<span data-size="down" style="font-size:0.8em">${richHtml(tok.slice(2, -2))}</span>`;
    else out += `<i>${richHtml(tok.slice(1, -1))}</i>`;
    last = m.index + tok.length;
  }
  if (last < text.length) out += esc(text.slice(last));
  return out;
}

// ─── Editable DOM → runs ────────────────────────────────────────────────────

/**
 * The field's DOM as runs: <b>/<strong>/font-weight ≥ 600 → bold, <i>/<em>/italic → italic, sized spans, style
 * spans (data-st), <br> and Enter-made <div>/<p> → line breaks (a leading one dropped).
 */
export function domToRuns(root: Node): Run[] {
  const out: Run[] = [];
  const walk = (node: Node, a: Omit<Run, 'text'>) => {
    if (node.nodeType === 3) {
      const parts = (node.textContent || '').replace(/\u00a0/g, ' ').split('\n');
      parts.forEach((p, i) => { if (i) out.push({ text: '\n' }); if (p) out.push({ text: p, ...a }); });
      return;
    }
    if (node.nodeType !== 1 && node.nodeType !== 11) return;
    if (node.nodeType === 11) { node.childNodes.forEach((c) => walk(c, a)); return; }
    const el = node as HTMLElement; const tag = el.tagName.toLowerCase();
    if (tag === 'br') { out.push({ text: '\n' }); return; }
    if (tag === 'div' || tag === 'p') out.push({ text: '\n' });
    const next: Omit<Run, 'text'> = { ...a };
    if (el.dataset?.st !== undefined) next.st = { ...a.st, ...parseStyle(el.dataset.st) };
    else {
      if (tag === 'b' || tag === 'strong' || /^(bold|[6-9]00)$/.test(el.style?.fontWeight || '')) next.b = true;
      if (tag === 'i' || tag === 'em' || el.style?.fontStyle === 'italic') next.it = true;
    }
    if (el.dataset?.size === 'up' || el.dataset?.size === 'down') next.sz = el.dataset.size;
    el.childNodes.forEach((c) => walk(c, next));
  };
  // The field itself is not content: its own tag and style (a bold title) are the field's look, not markup.
  root.childNodes.forEach((c) => walk(c, {}));
  if (out[0]?.text === '\n') out.shift();
  return out;
}

/** Editable HTML → markup. */
export function htmlToMarkup(root: HTMLElement): string {
  return runsToMarkup(domToRuns(root));
}

// ─── Formatting a range of runs ─────────────────────────────────────────────

export const runsLength = (runs: Run[]) => runs.reduce((n, r) => n + r.text.length, 0);

/** Apply `fn` to the characters [start, end) — runs are split at the edges; line breaks are left alone. */
export function mapRange(runs: Run[], start: number, end: number, fn: (r: Run) => Run): Run[] {
  const out: Run[] = []; let pos = 0;
  for (const r of runs) {
    const a = pos; const b = pos + r.text.length; pos = b;
    if (b <= start || a >= end || r.text === '\n') { out.push(r); continue; }
    const s = Math.max(start, a) - a; const e = Math.min(end, b) - a;
    if (s > 0) out.push({ ...r, text: r.text.slice(0, s) });
    out.push(fn({ ...r, text: r.text.slice(s, e) }));
    if (e < r.text.length) out.push({ ...r, text: r.text.slice(e) });
  }
  return normalizeRuns(out);
}
const inRange = (runs: Run[], start: number, end: number) => {
  const hit: Run[] = []; let pos = 0;
  for (const r of runs) { const a = pos; pos += r.text.length; if (r.text !== '\n' && pos > start && a < end) hit.push(r); }
  return hit;
};

export type FormatOp =
  | { kind: 'bold' } | { kind: 'italic' } | { kind: 'size'; sz: 'up' | 'down' }
  | { kind: 'style'; patch: { c?: string | null; w?: number | null; f?: string | null } }
  | { kind: 'clear' };

/** One formatting action over [start, end). Toggles follow Slides: all bold → unbold, otherwise bold. */
export function formatRuns(runs: Run[], start: number, end: number, op: FormatOp): Run[] {
  const hit = inRange(runs, start, end);
  const isBold = (r: Run) => !!r.b || (r.st?.w ?? 0) >= 600;
  const isItalic = (r: Run) => !!r.it || !!r.st?.i;
  switch (op.kind) {
    case 'bold': {
      const on = !(hit.length && hit.every(isBold));
      return mapRange(runs, start, end, (r) => ({ ...r, b: on, st: r.st?.w ? { ...r.st, w: undefined } : r.st }));
    }
    case 'italic': {
      const on = !(hit.length && hit.every(isItalic));
      return mapRange(runs, start, end, (r) => ({ ...r, it: on, st: r.st?.i ? { ...r.st, i: undefined } : r.st }));
    }
    case 'size': {
      const on = !(hit.length && hit.every((r) => r.sz === op.sz));
      return mapRange(runs, start, end, (r) => ({ ...r, sz: on ? op.sz : undefined }));
    }
    case 'style':
      return mapRange(runs, start, end, (r) => {
        const st: SpanStyle = { ...r.st };
        for (const [k, v] of Object.entries(op.patch) as Array<[keyof SpanStyle, any]>) { if (v === null || v === undefined || v === '') delete st[k]; else (st as any)[k] = v; }
        // An explicit weight replaces bold (so "Regular" on bold text un-bolds it).
        return { ...r, b: op.patch.w != null ? false : r.b, st };
      });
    case 'clear':
      return mapRange(runs, start, end, (r) => ({ text: r.text }));
  }
}

/** The style shared by every run in [start, end) (what the format bar shows as current). */
export function styleAt(runs: Run[], start: number, end: number): { c?: string; w?: number; f?: string; bold: boolean; italic: boolean } {
  const hit = inRange(runs, start, Math.max(end, start + 1));
  const same = <T>(get: (r: Run) => T | undefined) => { const v = hit.map(get); return v.length && v.every((x) => x === v[0]) ? v[0] : undefined; };
  return {
    c: same((r) => r.st?.c), w: same((r) => r.st?.w ?? (r.b ? 700 : undefined)), f: same((r) => r.st?.f),
    bold: !!hit.length && hit.every((r) => !!r.b || (r.st?.w ?? 0) >= 600), italic: !!hit.length && hit.every((r) => !!r.it || !!r.st?.i),
  };
}

// ─── Selection offsets in an editable field ─────────────────────────────────

/** The selection inside `root` as character offsets over its runs; null when the selection is elsewhere. */
export function selectionOffsets(root: HTMLElement): { start: number; end: number } | null {
  const sel = window.getSelection();
  if (!sel || !sel.rangeCount) return null;
  const range = sel.getRangeAt(0);
  if (!root.contains(range.startContainer) || !root.contains(range.endContainer)) return null;
  const upTo = (node: Node, offset: number) => {
    const r = document.createRange(); r.selectNodeContents(root); r.setEnd(node, offset);
    const holder = document.createElement('div'); holder.appendChild(r.cloneContents());
    return runsLength(domToRuns(holder));
  };
  const start = upTo(range.startContainer, range.startOffset);
  const end = upTo(range.endContainer, range.endOffset);
  return { start: Math.min(start, end), end: Math.max(start, end) };
}

/** Select characters [start, end) in a field rendered by richHtml (text nodes + <br>). */
export function setSelectionOffsets(root: HTMLElement, start: number, end: number): void {
  let pos = 0;
  const find = (target: number): { node: Node; offset: number } => {
    let found: { node: Node; offset: number } | null = null;
    const walk = (node: Node) => {
      if (found) return;
      if (node.nodeType === 3) {
        const len = (node.textContent || '').length;
        if (target <= pos + len) { found = { node, offset: target - pos }; return; }
        pos += len; return;
      }
      if ((node as HTMLElement).tagName === 'BR') {
        if (target <= pos) { const p = node.parentNode!; found = { node: p, offset: Array.prototype.indexOf.call(p.childNodes, node) }; return; }
        pos += 1; return;
      }
      node.childNodes.forEach(walk);
    };
    pos = 0; walk(root);
    return found || { node: root, offset: root.childNodes.length };
  };
  const a = find(start); const b = find(end);
  const sel = window.getSelection(); if (!sel) return;
  const r = document.createRange(); r.setStart(a.node, a.offset); r.setEnd(b.node, b.offset);
  sel.removeAllRanges(); sel.addRange(r);
}
